import React, { useMemo, useState } from 'react';
import {
  REGISTRY, LEVELS, LEGACY_ADMIN_DEFAULT, normalize, grantOf, can, summarize, topLevel, accessLabel, describe,
} from './adminPermissions';

// Admin Access — the only place access can change.
//
// Safety design: there is deliberately NO browsable list of candidates here. You
// must type a search that matches a specific person before any grant control
// appears, you pick the tier (and the cards) explicitly, and granting takes a
// confirmation naming the account. That makes it very hard to promote the wrong
// person by mis-clicking a row in a long list, which is why the Users directory
// is read-only.
//
// Since 10-07 (migration 026) an admin's access is per card: each tab is either
// full access or a selection of cards, with a level on Alerts and the Review
// queue. The editor below is used both when granting and on an existing admin.
//
// Who can be here: elevated admins, and admins holding this card. The latter
// work within limits the server enforces too - they cannot touch an elevated
// admin, cannot promote to elevated, and cannot grant a card beyond what they
// hold themselves (so access can't be handed out sideways).

const MIN_QUERY = 2;

const TIERS = [
  {
    id: 'admin',
    label: 'Admin',
    blurb: 'Access to the cards you pick below, at the level you set for each.',
  },
  {
    id: 'superadmin',
    label: 'Elevated admin',
    blurb: 'Full access to every tab and card, including releasing alerts and changing access. Cannot be narrowed.',
  },
];

const PRESETS = [
  { id: 'standard', label: 'Standard admin', perms: LEGACY_ADMIN_DEFAULT, blurb: 'Read every tab, author and submit alerts, see the review queue, announcements, intake, user list.' },
  { id: 'full', label: 'Full access, all tabs', perms: Object.fromEntries(REGISTRY.map((t) => [t.id, { full: true }])), blurb: 'Everything an elevated admin can do, but as a narrowable admin.' },
  { id: 'none', label: 'Start empty', perms: {}, blurb: 'Pick the cards one by one.' },
];

const TIER_BADGE = {
  admin: 'bg-gray-100 text-gray-600',
  superadmin: 'bg-blue-100 text-blue-700',
};

/**
 * Per-tab / per-card editor. `limit` is the actor's own map when they are not
 * elevated: anything beyond it is disabled, matching the server rule.
 */
export function AccessEditor({ value, onChange, limit }) {
  const perms = normalize(value);
  const allowed = (tab, card, level) => !limit || can(limit, tab, card, level);
  const allowedFull = (tab) => !limit || REGISTRY.find((t) => t.id === tab).cards.every((c) => allowed(tab, c.id, c.levels ? topLevel(c) : undefined));

  const setTab = (tab, patch) => onChange(normalize({ ...perms, [tab]: { ...perms[tab], ...patch } }));
  const setCard = (tab, card, v) => setTab(tab, { full: false, cards: { ...(perms[tab].cards || {}), [card]: v } });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
      {REGISTRY.map((tab) => {
        const t = perms[tab.id];
        return (
          <div key={tab.id} className="border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-black">{tab.label}</p>
              <label className={`flex items-center gap-1.5 text-xs ${allowedFull(tab.id) ? 'text-gray-700 cursor-pointer' : 'text-gray-300'}`}>
                <input
                  type="checkbox"
                  checked={t.full}
                  disabled={!allowedFull(tab.id)}
                  onChange={(e) => setTab(tab.id, e.target.checked
                    ? { full: true }
                    : { full: false, cards: Object.fromEntries(tab.cards.map((c) => [c.id, c.levels ? 'none' : false])) })}
                />
                Full access
              </label>
            </div>
            <div className={`space-y-1.5 ${t.full ? 'opacity-40' : ''}`}>
              {tab.cards.map((c) => {
                const v = grantOf(perms, tab.id, c.id);
                if (c.levels) {
                  return (
                    <div key={c.id} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-gray-700">{c.label}</span>
                      <select
                        className="border border-gray-200 rounded px-1.5 py-1 text-xs bg-white max-w-[55%]"
                        value={v}
                        disabled={t.full}
                        onChange={(e) => setCard(tab.id, c.id, e.target.value)}
                      >
                        {LEVELS[c.levels].map((lvl) => (
                          <option key={lvl} value={lvl} disabled={lvl !== 'none' && !allowed(tab.id, c.id, lvl)}>
                            {c.levelLabels[lvl]}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }
                const ok = allowed(tab.id, c.id);
                return (
                  <label key={c.id} className={`flex items-center gap-2 text-xs ${ok ? 'text-gray-700 cursor-pointer' : 'text-gray-300'}`}>
                    <input
                      type="checkbox"
                      checked={v === true}
                      disabled={t.full || !ok}
                      onChange={(e) => setCard(tab.id, c.id, e.target.checked)}
                    />
                    {c.label}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Expandable per-tab breakdown of one admin's access (10-07). Elevated admins
// get the same view with everything on, so the two tiers read alike.
function AccessDetail({ role, perms, open, onToggle }) {
  const full = role === 'superadmin';
  const detail = describe(full ? Object.fromEntries(REGISTRY.map((t) => [t.id, { full: true }])) : (perms || LEGACY_ADMIN_DEFAULT));
  return (
    <div className="mt-1">
      <button type="button" onClick={onToggle} className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1">
        {open ? 'Hide access' : 'Show access'}
        <span className={`inline-block transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {detail.map((t) => (
            <div key={t.tab} className="border border-gray-100 rounded-lg px-3 py-2 bg-gray-50/60">
              <p className="text-xs font-semibold text-black mb-1">
                {t.label}
                {t.full && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">Full access</span>}
              </p>
              <ul className="space-y-0.5">
                {t.cards.map((c) => (
                  <li key={c.label} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className={c.granted ? 'text-gray-700' : 'text-gray-400'}>{c.label}</span>
                    <span className={`flex-shrink-0 ${c.granted ? 'text-green-700 font-medium' : 'text-gray-300'}`}>{c.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccessSummary({ perms }) {
  const lines = summarize(perms);
  if (!lines.length) return <p className="text-[11px] text-red-600 mt-0.5">No cards — this admin sees nothing in the Admin area.</p>;
  return (
    <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
      {lines.map((l, i) => <span key={l}>{i > 0 ? ' · ' : ''}{l}</span>)}
    </p>
  );
}

export default function AdminAccess({ Section, users, meEmail, setRole, setPermissions, isElevated, myPermissions }) {
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('admin');
  const [preset, setPreset] = useState('standard');
  const [grantPerms, setGrantPerms] = useState(normalize(LEGACY_ADMIN_DEFAULT));
  const [customize, setCustomize] = useState(false);
  const [err, setErr] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [openId, setOpenId] = useState(null); // whose access breakdown is expanded
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // A non-elevated manager cannot grant beyond their own access.
  const limit = isElevated ? null : myPermissions;

  const admins = useMemo(
    () => users
      .filter((u) => u.role === 'admin' || u.role === 'superadmin')
      // Elevated first, then by name — the people who can release alerts matter most.
      .sort((a, b) => (b.role === 'superadmin') - (a.role === 'superadmin')
        || (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '')),
    [users]
  );

  const needle = q.trim().toLowerCase();
  const searchReady = needle.length >= MIN_QUERY;

  // Only plain users are grant candidates; existing admins are managed in the
  // list above, where the current access is visible.
  const matches = useMemo(() => {
    if (!searchReady) return [];
    return users
      .filter((u) => u.role !== 'admin' && u.role !== 'superadmin')
      .filter((u) => [u.display_name, u.email].some((f) => (f || '').toLowerCase().includes(needle)))
      .slice(0, 10);
  }, [users, needle, searchReady]);

  const apply = async (u, role, confirmText, perms) => {
    setErr(null);
    if (!window.confirm(confirmText)) return false;
    const error = await setRole(u, role, perms);
    if (error) { setErr(error); return false; }
    return true;
  };

  const choosePreset = (id) => {
    setPreset(id);
    setGrantPerms(normalize(PRESETS.find((p) => p.id === id).perms));
  };

  const grant = async (u) => {
    const t = TIERS.find((x) => x.id === tier);
    const label = `${u.display_name ? `${u.display_name} — ` : ''}${u.email}`;
    const detail = tier === 'admin'
      ? `Access:\n${summarize(grantPerms).map((l) => `  • ${l}`).join('\n') || '  (no cards)'}`
      : t.blurb;
    if (await apply(u, tier, `Grant ${t.label} access to:\n\n${label}\n\n${detail}`, tier === 'admin' ? grantPerms : undefined)) setQ('');
  };

  const startEdit = (u) => {
    setErr(null);
    setEditingId(u.id);
    setDraft(normalize(u.admin_permissions || LEGACY_ADMIN_DEFAULT));
  };

  const saveEdit = async (u) => {
    setSaving(true); setErr(null);
    const error = await setPermissions(u, draft);
    setSaving(false);
    if (error) { setErr(error); return; }
    setEditingId(null); setDraft(null);
  };

  const input = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';
  const elevatedCount = admins.filter((u) => u.role === 'superadmin').length;

  // What the current viewer may do to a given admin row.
  const rowLocked = (u) => {
    if (u.email === meEmail) return 'can’t change your own access';
    if (u.role === 'superadmin' && !isElevated) return 'elevated admins only';
    return null;
  };

  return (
    <Section title={`Admin Access (${admins.length})`}>
      <p className="text-xs text-gray-400 mb-3">
        {elevatedCount} elevated · {admins.length - elevatedCount} admin{admins.length - elevatedCount === 1 ? '' : 's'} with
        per-card access. Elevated admins have every card and are the only ones who can change another elevated admin.
      </p>

      {/* Current admins — short by nature, and revoking has to live somewhere. */}
      {admins.length === 0 ? (
        <p className="text-gray-400 text-sm">No admins yet.</p>
      ) : (
        <div className="divide-y divide-gray-100 mb-5">
          {admins.map((u) => {
            const locked = rowLocked(u);
            const editing = editingId === u.id;
            return (
              <div key={u.id} className="py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-black truncate">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium mr-2 ${TIER_BADGE[u.role]}`}>
                        {u.role === 'superadmin' ? 'Elevated · Full access' : accessLabel(u.role, u.admin_permissions)}
                      </span>
                      {u.display_name || u.email}
                      {u.email === meEmail && <span className="text-gray-400"> (you)</span>}
                    </p>
                    {u.display_name && <p className="text-[11px] text-gray-400 truncate mt-0.5">{u.email}</p>}
                    {!editing && (
                      <AccessDetail
                        role={u.role}
                        perms={u.admin_permissions}
                        open={openId === u.id}
                        onToggle={() => setOpenId((id) => (id === u.id ? null : u.id))}
                      />
                    )}
                  </div>
                  {locked ? (
                    <span className="text-xs text-gray-300 flex-shrink-0">{locked}</span>
                  ) : (
                    <div className="flex gap-3 flex-shrink-0">
                      {u.role === 'admin' && !editing && (
                        <button onClick={() => startEdit(u)} className="text-xs text-blue-600 hover:underline">
                          Edit access
                        </button>
                      )}
                      {u.role === 'superadmin' ? (
                        <button
                          onClick={() => apply(u, 'admin', `Reduce ${u.email} to admin?\n\nThey keep the standard admin cards (author alerts, read everything) but can no longer release alerts or change access. You can narrow their cards afterwards.`, LEGACY_ADMIN_DEFAULT)}
                          className="text-xs text-amber-700 hover:underline"
                        >
                          Reduce to admin
                        </button>
                      ) : isElevated ? (
                        <button
                          onClick={() => apply(u, 'superadmin', `Promote ${u.email} to elevated admin?\n\nThey will have full access to every card, including releasing fraud alerts and granting or revoking access.`)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Make elevated
                        </button>
                      ) : null}
                      <button
                        onClick={() => apply(u, 'user', `Revoke all admin access from ${u.email}?`)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Revoke
                      </button>
                    </div>
                  )}
                </div>

                {editing && (
                  <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <AccessEditor value={draft} onChange={setDraft} limit={limit} />
                    <div className="flex items-center gap-3 mt-4">
                      <button
                        onClick={() => saveEdit(u)}
                        disabled={saving}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                      >
                        {saving ? 'Saving…' : 'Save access'}
                      </button>
                      <button onClick={() => { setEditingId(null); setDraft(null); }} className="text-sm text-gray-500 hover:underline">
                        Cancel
                      </button>
                      <span className="text-[11px] text-gray-400">Takes effect on their next page load.</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}

      {/* Grant — search-gated, no browsable list */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-black mb-1">Grant admin access</p>
        <p className="text-xs text-gray-400 mb-3">
          Pick the tier and the cards, then search for the exact person by name or email. Nothing is listed
          until you search, so a user can’t be promoted by accident.
        </p>

        <div className="flex flex-col gap-1.5 mb-3">
          {TIERS.map((t) => {
            const disabled = t.id === 'superadmin' && !isElevated;
            return (
              <label key={t.id} className={`flex items-start gap-2 text-sm ${disabled ? 'opacity-40' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  name="grant-tier"
                  className="mt-1"
                  checked={tier === t.id}
                  disabled={disabled}
                  onChange={() => setTier(t.id)}
                />
                <span>
                  <span className="text-black">{t.label}</span>
                  <span className="block text-[11px] text-gray-400">
                    {t.blurb}{disabled ? ' Only an elevated admin can grant this.' : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>

        {tier === 'admin' && (
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs text-gray-500">Start from</span>
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => choosePreset(p.id)}
                  title={p.blurb}
                  className={`px-2 py-1 rounded text-xs ${preset === p.id ? 'bg-blue-600 text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
                >
                  {p.label}
                </button>
              ))}
              <button type="button" onClick={() => setCustomize((v) => !v)} className="text-xs text-blue-600 hover:underline ml-auto">
                {customize ? 'Hide cards' : 'Customize cards'}
              </button>
            </div>
            {customize ? (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <AccessEditor value={grantPerms} onChange={(v) => { setGrantPerms(v); setPreset(null); }} limit={limit} />
              </div>
            ) : (
              <AccessSummary perms={grantPerms} />
            )}
          </div>
        )}

        <input
          className={input}
          placeholder="Search name or email to promote…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {!searchReady ? (
          <p className="text-xs text-gray-400 mt-2">
            Type at least {MIN_QUERY} characters to search.
          </p>
        ) : matches.length === 0 ? (
          <p className="text-sm text-gray-400 mt-3">No non-admin user matches “{q.trim()}”.</p>
        ) : (
          <div className="divide-y divide-gray-100 mt-3">
            {matches.map((u) => (
              <div key={u.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-black truncate">{u.display_name || '—'}</p>
                  <p className="text-[11px] text-gray-400 truncate">{u.email}</p>
                </div>
                {u.email === meEmail ? (
                  <span className="text-xs text-gray-300 flex-shrink-0">you</span>
                ) : (
                  <button
                    onClick={() => grant(u)}
                    className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 flex-shrink-0"
                  >
                    Grant {TIERS.find((t) => t.id === tier).label}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
